import { FC, useEffect } from 'react';

import { useTranslations } from '@lang';
import { useFormContext, useWatch } from 'react-hook-form';
import { Container } from 'styled-bootstrap-grid';

import { Button, ErrorBox, InfoBox, StickyBarContent } from '@common/components';
import Portal from '@common/components/Portal';

import { SpaEntityFormValues } from '@common/components/SpaEntity/SpaEntityForm.schema';
import { EntityTypes } from '@common-types/Entities';
import { AddGuestsForm } from './AddGuestsForm';
import { GuestDetailsProps } from './GuestDetails.types';

export const GuestDetails: FC<GuestDetailsProps> = ({
  spaEntity,
  typeName = 'Spa Treatment',
  entityType,
  stickyBarId,
  onSubmit,
  existingGuests = [],
  handleInvalidConfig,
  guest = null,
  showGenderNeutral,
  isItemFetching,
}) => {
  const { t } = useTranslations();
  const { handleSubmit, formState, setValue } = useFormContext<SpaEntityFormValues>();
  const selectedArrivalTime = useWatch({ name: 'arrivalTime' });
  const selectedGuests = useWatch({ name: 'guests' });

  useEffect(() => {
    if (!selectedGuests?.length) {
      handleInvalidConfig();
    }
  }, [selectedGuests, handleInvalidConfig]);

  const getCalculatedPrice = () => ({
    ...selectedArrivalTime.price,
    amount: selectedArrivalTime.price.amount * (selectedGuests?.length ?? 1),
  });

  const getCalculatedDiscount = () =>
    selectedArrivalTime.discount
      ? {
          discountedPrice: {
            ...selectedArrivalTime.discount?.discountedPrice,
            amount:
              selectedArrivalTime.discount?.discountedPrice.amount * (selectedGuests?.length ?? 1),
          },
        }
      : undefined;

  const { availability, extras, minimumGuests, selectionGroups, maximumGuests } = spaEntity || {};
  // The selected arrival time carries the live availability for that specific slot. Prefer it over
  // the entity's availability list, which is only fetched for the initial date and won't contain
  // slots the guest picks on other days of the week.
  const timeslotMaxGuests =
    selectedArrivalTime && availability
      ? availability?.find(
          (arrivalTime) => selectedArrivalTime.startDateTime === arrivalTime.startDateTime
        )
      : null;

  const maxGuests = maximumGuests ?? minimumGuests ?? 1;
  const numberAvailable =
    selectedArrivalTime?.numberAvailable ?? timeslotMaxGuests?.numberAvailable ?? maxGuests;
  const effectiveMaxGuests = Math.min(numberAvailable, maxGuests);
  const isExtrasAvailable = extras?.length || selectionGroups?.length;
  const hasEnoughQuantity =
    !spaEntity || (!!spaEntity && effectiveMaxGuests >= (minimumGuests ?? 0));

  const Gender = { M: 'male', F: 'female' };
  const defaultGender = spaEntity?.genderSpecific ? Gender[spaEntity.genderSpecific] || '' : '';

  // If the guest already added more people than the selected slot now allows (e.g. after switching
  // to a lower-availability time), trim the list down to the effective maximum.
  useEffect(() => {
    if (hasEnoughQuantity && (selectedGuests?.length ?? 0) > effectiveMaxGuests) {
      setValue('guests', selectedGuests.slice(0, effectiveMaxGuests));
    }
  }, [hasEnoughQuantity, effectiveMaxGuests, selectedGuests, setValue]);

  return selectedArrivalTime ? (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Container fluid>
        {!hasEnoughQuantity && (
          <ErrorBox error={t('spa.booking.insufficientAvailability')} />
        )}
        <AddGuestsForm
          existingGuests={existingGuests}
          guest={guest as any}
          hasEnoughQuantity={hasEnoughQuantity}
          minimumGuests={minimumGuests}
          maximumGuests={effectiveMaxGuests}
          spaEntity={spaEntity}
          showGenderNeutral={showGenderNeutral}
          typeName={typeName}
          isItemFetching={isItemFetching}
          defaultGender={defaultGender}
        />
        {entityType === EntityTypes.TREATMENT && <InfoBox>{t('spa.timeslotsWarning')}</InfoBox>}
      </Container>
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={spaEntity.name}
          displayedPrice={getCalculatedPrice() as any}
          discountedPrice={getCalculatedDiscount()}
          button={
            <Button
              isPrimary
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={!hasEnoughQuantity || formState.isSubmitting}
            >
              {!!isExtrasAvailable && t('treatment.customizeTreatmentsCTA')}
              {!isExtrasAvailable && t('treatment.addToBasketCTA')}
            </Button>
          }
        />
      </Portal>
    </form>
  ) : null;
};
